import { Data } from '@angular/router';

import { PlatformPermission, PermissionCriteria } from '../models/permission.model';
import { PermissionMatchMode, RoutePermissionData } from '../models/route-permission-data.model';

export function hasPermission(userPermissions: unknown, permission: PlatformPermission): boolean {
  const normalizedUserPermissions = normalizePermissionCodes(userPermissions);
  const normalizedPermission = normalizePermissionCode(permission);

  if (!normalizedPermission || normalizedUserPermissions.length === 0) {
    return false;
  }

  return normalizedUserPermissions.includes(normalizedPermission);
}

export function hasAnyPermission(
  userPermissions: unknown,
  permissions: ReadonlyArray<PlatformPermission> | null | undefined
): boolean {
  const normalizedUserPermissions = normalizePermissionCodes(userPermissions);

  if (normalizedUserPermissions.length === 0) {
    return false;
  }

  if (!permissions?.length) {
    return true;
  }

  const normalizedPermissions = normalizePermissionCodes(permissions);

  if (normalizedPermissions.length === 0) {
    return true;
  }

  return normalizedPermissions.some((permission) => normalizedUserPermissions.includes(permission));
}

export function hasAllPermissions(
  userPermissions: unknown,
  permissions: ReadonlyArray<PlatformPermission> | null | undefined
): boolean {
  const normalizedUserPermissions = normalizePermissionCodes(userPermissions);

  if (normalizedUserPermissions.length === 0) {
    return false;
  }

  if (!permissions?.length) {
    return true;
  }

  const normalizedPermissions = normalizePermissionCodes(permissions);

  if (normalizedPermissions.length === 0) {
    return true;
  }

  return normalizedPermissions.every((permission) => normalizedUserPermissions.includes(permission));
}

export function matchesPermissionCriteria(
  userPermissions: unknown,
  criteria: PermissionCriteria,
  mode: PermissionMatchMode = 'all'
): boolean {
  const permissions = Array.isArray(criteria) ? criteria : [criteria];

  if (!permissions.length) {
    return true;
  }

  if (mode === 'any') {
    return hasAnyPermission(userPermissions, permissions);
  }

  return hasAllPermissions(userPermissions, permissions);
}

export function getRoutePermissionData(routeData: Data | undefined): RoutePermissionData | null {
  if (!routeData) {
    return null;
  }

  const permissions = routeData['permissions'];
  const mode = routeData['permissionMode'];

  if (!Array.isArray(permissions)) {
    return null;
  }

  return {
    permissions: permissions as ReadonlyArray<PlatformPermission>,
    mode: mode === 'any' ? 'any' : 'all'
  };
}

function normalizePermissionCodes(source: unknown): ReadonlyArray<string> {
  if (!source) {
    return [];
  }

  if (typeof source === 'string') {
    const normalizedCode = normalizePermissionCode(source);
    return normalizedCode ? [normalizedCode] : [];
  }

  if (!Array.isArray(source)) {
    return [];
  }

  const normalizedCodes = new Set<string>();

  for (const entry of source) {
    if (typeof entry === 'string') {
      const normalizedCode = normalizePermissionCode(entry);

      if (normalizedCode) {
        normalizedCodes.add(normalizedCode);
      }

      continue;
    }

    if (entry && typeof entry === 'object') {
      const entryRecord = entry as Record<string, unknown>;
      const codeCandidate =
        normalizePermissionCode(entryRecord['permission_code']) ??
        normalizePermissionCode(entryRecord['code']) ??
        normalizePermissionCode(entryRecord['name']) ??
        normalizePermissionCode(entryRecord['permission']) ??
        normalizePermissionCode(entryRecord['authority']);

      if (codeCandidate) {
        normalizedCodes.add(codeCandidate);
      }
    }
  }

  return Array.from(normalizedCodes);
}

function normalizePermissionCode(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase().replace(/[.\s-]+/g, '_');
  return normalizedValue.length > 0 ? normalizedValue : null;
}
